import { Box, Tab, Tabs, Typography } from '@mui/material';
import type { ReactElement } from 'react';

import { hasValue } from '@core/utils/equality';

import { SquaredBadge } from '../SquaredBadge/SquaredBadge';

export type SlabTabItem<T extends string> = {
  value: T;
  label: string;
  count?: number;
  maxCount?: number;
  subtitle?: string;
  id?: string;
  panelId?: string;
};

type Props<T extends string> = {
  value: T;
  items: SlabTabItem<T>[];
  onChange: (value: T) => void;
  disabled?: boolean;
};

export const SlabTabs = <T extends string>({
  value,
  items,
  onChange,
  disabled,
}: Props<T>): ReactElement => (
  <Tabs
    value={value}
    onChange={(_, val: T) => onChange(val)}
    variant='scrollable'
    scrollButtons='auto'
    allowScrollButtonsMobile
    sx={{
      px: 1,
      minHeight: 'unset',
      '& .MuiTabs-flexContainer': { gap: 2 },
      '& .MuiTab-root': {
        minWidth: 0,
        minHeight: 'unset',
        fontSize: 16,
        fontWeight: 400,
        textTransform: 'none',
      },
    }}
  >
    {items.map((item) => {
      const isActive = item.value === value;
      const { count, maxCount = 99 } = item;
      const titleRow = (
        <Box display='flex' alignItems='center' gap={1}>
          {item.label}
          {hasValue(count) && count > 0 && (
            <SquaredBadge
              badgeContent={count > maxCount ? `${maxCount}+` : count.toLocaleString('en-US')}
              textColor={isActive ? 'SlabGray.0' : 'SlabGray.600'}
              backgroundColor={isActive ? 'SlabIndigo.500' : 'SlabGray.Stroke'}
            />
          )}
        </Box>
      );
      return (
        <Tab
          key={item.value}
          value={item.value}
          id={item.id}
          aria-controls={item.panelId}
          disabled={disabled}
          label={
            hasValue(item.subtitle) ? (
              <Box display='flex' flexDirection='column' alignItems='center'>
                {titleRow}
                <Typography variant='body2' color='text.secondary'>
                  {item.subtitle}
                </Typography>
              </Box>
            ) : (
              titleRow
            )
          }
        />
      );
    })}
  </Tabs>
);
